import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import "./HowItWorks.css";

const STEPS = [
  {
    number: "01",
    title: "Pick a merchant",
    text: "Choose the store you want to shop from. GenieShop opens a dedicated chat scoped to that store.",
  },
  {
    number: "02",
    title: "Ask Genie",
    text: "Describe what you're looking for in plain language. Genie displays relevant unbiased unsponsored product cards right in the chat.",
  },
  {
    number: "03",
    title: "Rank & compare",
    text: "Ask Genie to rank results against your own criteria: lowest price, best rating, or anything you type, backed by live web research.",
  },
  {
    number: "04",
    title: "Get an AI summary",
    text: "Tap any product for a researched breakdown: description, key specs, pros and cons, with sources.",
  },
  {
    number: "05",
    title: "Cart & checkout",
    text: "Add items to your cart and check out with Stripe, without ever leaving the conversation.",
  },
];

const GAP_PX = 20; // keep in sync with CSS .how-it-works-track gap

const HowItWorks = () => {
  const scrollRef = useRef(null);
  const cardRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  // Number of distinct scroll positions the row can actually reach — computed
  // from real layout, not STEPS.length, since several trailing cards can share
  // the same (clamped) max-scroll position depending on viewport width.
  const [dotCount, setDotCount] = useState(1);

  const measure = useCallback(() => {
    const container = scrollRef.current;
    const card = cardRef.current;
    if (!container || !card) return;

    const step = card.offsetWidth + GAP_PX;
    const maxScroll = Math.max(0, container.scrollWidth - container.clientWidth);

    if (maxScroll <= 0 || step <= 0) {
      setDotCount(1);
      setActiveIndex(0);
      return;
    }

    const exactStops = Math.floor(maxScroll / step) + 1;
    const lastExactValue = (exactStops - 1) * step;
    const hasExtraStop = lastExactValue < maxScroll - 1; // small tolerance for subpixel rounding
    const count = Math.min(exactStops + (hasExtraStop ? 1 : 0), STEPS.length);

    setDotCount(count);
    setActiveIndex((prev) => Math.min(prev, count - 1));
  }, []);

  useLayoutEffect(() => {
    measure();
    // Re-measure after webfonts finish swapping in, which can shift card widths.
    const t = window.setTimeout(measure, 300);
    window.addEventListener("resize", measure);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener("resize", measure);
    };
  }, [measure]);

  useEffect(() => {
    const container = scrollRef.current;
    const card = cardRef.current;
    if (!container || !card) return;

    const step = card.offsetWidth + GAP_PX;
    const maxScroll = Math.max(0, container.scrollWidth - container.clientWidth);
    const left = Math.min(activeIndex * step, maxScroll);
    container.scrollTo({ left, behavior: "smooth" });
  }, [activeIndex]);

  const goPrev = () => {
    setActiveIndex((prev) => (prev - 1 + dotCount) % dotCount);
  };

  const goNext = () => {
    setActiveIndex((prev) => (prev + 1) % dotCount);
  };

  return (
    <div className="how-it-works reveal-on-scroll">
      <div className="how-it-works-header-row">
        <button className="how-it-works-arrow" aria-label="Previous step" onClick={goPrev}>
          &#8592;
        </button>
        <h2 className="section-title how-it-works-title">How GenieShop Works</h2>
        <button className="how-it-works-arrow" aria-label="Next step" onClick={goNext}>
          &#8594;
        </button>
      </div>

      <div className="how-it-works-scroll" ref={scrollRef}>
        <div className="how-it-works-track">
          {STEPS.map((step, idx) => (
            <div
              className="how-it-works-card"
              key={step.number}
              ref={idx === 0 ? cardRef : null}
            >
              <span className="how-it-works-number">{step.number}</span>
              <h3>{step.title}</h3>
              <p>{step.text}</p>
            </div>
          ))}
        </div>
      </div>

      {dotCount > 1 && (
        <div className="how-it-works-dots" aria-hidden>
          {Array.from({ length: dotCount }).map((_, idx) => (
            <span
              key={idx}
              className={`dot${idx === activeIndex ? " is-active" : ""}`}
              onClick={() => setActiveIndex(idx)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default HowItWorks;
